import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import validator from 'validator';
import {List} from 'immutable';

import {getModelForResourceType} from 'resources/modelRegistry';
import {SearchInput, addGenericErrorToast} from '@albert-io/atomic';

import {mandarkEndpoint} from '@albert-io/json-api-framework/request/builder/legacy';

import SchoolFilters from './SchoolChipFilters.react';

import SchoolSearchResults from './SchoolSearchResults.react';

const getSearchSchoolsByNameQuery = (searchString, country, state, zipCode) => {
  const isUUID = validator.isUUID(searchString);

  return mandarkEndpoint(['schools_v5'])
    .include('district_v1')
    .filter(
      {
        id: searchString
      },
      isUUID
    )
    .filter(
      {
        name: {
          case_insensitive_substring: searchString
        }
      },
      !isUUID
    )
    .filter(
      {
        /**
         * @see https://github.com/albert-io/project-management/issues/4314
         */
        country: encodeURIComponent(country)
      },
      !isUUID && country
    )
    .filter(
      {
        state
      },
      !isUUID && state
    )
    .filter(
      {
        postal_code: zipCode
      },
      !isUUID && zipCode
    )
    .sort('name', !isUUID)
    .customQuery(
      {
        sort_context: {
          search_by_name: {
            name: searchString
          }
        }
      },
      !isUUID
    );
};

const SchoolSearchInputs = ({
  setSelected,
  selected,
  shouldShowHelpText,
  onSubmit,
  onClear,
  hasSelectionError = false,
  hasExistingRecord,
  errorMessage,
  multiSelect = false
}) => {
  const USA = {label: 'United States of America', value: 'United States of America'};
  const [schoolNameSearchValue, setSchoolNameSearchValue] = useState('');
  const [countrySearchValue, setCountrySearchValue] = useState(USA);
  const [stateSearchValue, setStateSearchValue] = useState(undefined);
  const [postalcodeSearchValue, setPostalcodeSearchValue] = useState(undefined);

  const [isLoadingResults, setIsLoadingResults] = useState(false);
  const [results, setResults] = useState(List());

  useEffect(() => {
    onClear?.();
    let isSubscribed = true;
    const fetchSchoolResults = async () => {
      try {
        const schoolsResults = await getSearchSchoolsByNameQuery(
          schoolNameSearchValue,
          countrySearchValue?.value,
          stateSearchValue?.value,
          postalcodeSearchValue
        ).getResourcePromise();
        if (isSubscribed) {
          setResults(schoolsResults);
          setIsLoadingResults(false);
        }
      } catch {
        addGenericErrorToast();
      }
    };
    fetchSchoolResults();
    return () => {
      isSubscribed = false;
    };
  }, [schoolNameSearchValue, countrySearchValue, stateSearchValue, postalcodeSearchValue]);

  return (
    <>
      <SearchInput
        className='flex u-width_100pc'
        placeholder='Search by school name'
        value={schoolNameSearchValue}
        onChange={(e, value) => setSchoolNameSearchValue(value)}
        onReset={() => setSchoolNameSearchValue('')}
      />

      <SchoolFilters
        countrySearchValue={countrySearchValue}
        setCountrySearchValue={setCountrySearchValue}
        stateSearchValue={stateSearchValue}
        setStateSearchValue={setStateSearchValue}
        postalcodeSearchValue={postalcodeSearchValue}
        setPostalcodeSearchValue={setPostalcodeSearchValue}
      />
      <SchoolSearchResults
        schoolNameSearchValue={schoolNameSearchValue}
        isLoadingResults={isLoadingResults}
        results={results}
        selected={selected}
        setSelected={setSelected}
        onSubmit={onSubmit}
        hasSelectionError={hasSelectionError}
        errorMessage={!!hasSelectionError && errorMessage}
        hasExistingRecord={hasExistingRecord}
        shouldShowHelpText={shouldShowHelpText}
        multiSelect={multiSelect}
      />
    </>
  );
};

SchoolSearchInputs.propTypes = {
  // only one school allowed for teacher view but multiple are allowed for admin view in dennis
  selected: PropTypes.oneOfType([
    PropTypes.instanceOf(getModelForResourceType('school_v5')),
    PropTypes.arrayOf(PropTypes.instanceOf(getModelForResourceType('school_v5')))
  ]),
  setSelected: PropTypes.func,
  onSubmit: PropTypes.func,
  shouldShowHelpText: PropTypes.bool,
  hasSelectionError: PropTypes.string,
  errorMessage: PropTypes.node,
  multiSelect: PropTypes.bool,
  hasExistingRecord: PropTypes.func,
  onClear: PropTypes.func
};

export default SchoolSearchInputs;
